import { gql } from '@apollo/client'

/*
 * The depot lookup behind Order Detail's distributor row (COM-889).
 *
 * A document of its own rather than a field on the order. `Order` carries a
 * `storeCode` and no depot name, and `Store` is a separate collection with no
 * edge from the order to it, so turning one into the other is a second
 * request whatever else changes. Doing it here rather than asking storefront
 * for a join keeps the change inside this repo.
 *
 * Three things about the shape are easy to get wrong, and all three are
 * properties of the deployed schema rather than of this file:
 *
 * - the return type is PaginatedStores, so the stores live one level down.
 *   The bare list belongs to the deprecated stores query beside it;
 * - the filter must carry exactly one member. joi puts an xor across
 *   FilterStoreInput's five fields, so adding isActive to narrow the result
 *   turns the request into a 400;
 * - pagination is optional and omitted. The filter is an exact match on a
 *   code, so the default page is already larger than the answer can be.
 *
 * No sessionUid, because the query declares none: the user schema exposes
 * getStores unscoped, and a depot's display name is not customer data. That is
 * a property of the field rather than a choice made here, so there is nothing
 * to weigh - adding the variable would fail validation, not tighten anything.
 *
 * Not a unique case in this repo either, despite how the order documents read:
 * getFiltersValues selects brands, which declares no session argument. Its
 * sibling categories is not an example - that field does take an optional
 * sessionUid - so brands is the precedent, singular.
 *
 * What is unique to the order documents is the hazard. order takes an optional
 * sessionUid and branches on it: with it the resolver scopes the lookup to the
 * session's customer, and with id alone it takes a legacy contract that
 * validates the session recorded on the order rather than the caller's. That
 * is a loss of caller scoping, not a loss of all scoping - the session
 * middleware still requires that order's own session to be active - but it is
 * a weaker check than the field can offer, and it is silent. Nothing of that
 * kind exists here.
 */
const getStoresQuery = gql`
  query GetStores($storefrontName: String!, $filter: FilterStoreInput) {
    getStores(storefrontName: $storefrontName, filter: $filter) {
      stores {
        code
        name
      }
    }
  }
`

export default getStoresQuery
